import { apiFetch, apiFetchJson } from "./apiClient";

export interface SessionDetails {
  session_id: number;
  campaign_id: number;
  status: string;
  dm_player_id: number;
  is_dm: boolean;
  is_participating: boolean;
  character_id: number | null;
  character_name: string | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
  are_notes_locked?: boolean;
  campaign_created_by?: string;
}

export interface SessionParticipant {
  id: number;
  user_id: number;
  username: string;
  user_name: string;
  character_id: number | null;
  character_name: string | null;
  is_dm: boolean;
  is_online: boolean;
  joined_at: string;
}

export interface SessionParticipantsResponse {
  session_id: number;
  participants: SessionParticipant[];
  total_count: number;
}

export interface PersistedChatMessage {
  id: number;
  session_id: number;
  user_id: number;
  sender_username: string;
  character_name: string;
  message_type: string; // "ic" | "ooc" | "dice_roll" | "system"
  content: string;
  is_part_of_log: boolean;
  sent_at: string;
}

export interface ChatMessagesResponse {
  session_id: number;
  messages: PersistedChatMessage[];
  count: number;
}

export interface SessionLogResponse {
  session_id: number;
  log: PersistedChatMessage[];
  count: number;
}

export const sessionService = {
  async getSessionDetails(sessionId: string): Promise<SessionDetails> {
    const res = await apiFetch(`/api/protected/sessions/${sessionId}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error("Session not found");
      if (res.status === 403) throw new Error("Access denied");
      throw new Error("Failed to fetch session details");
    }
    return res.json();
  },

  async getSessionParticipants(sessionId: string): Promise<SessionParticipantsResponse> {
    const res = await apiFetch(`/api/protected/sessions/${sessionId}/participants`);
    if (!res.ok) {
      if (res.status === 404) throw new Error("Session not found");
      if (res.status === 403) throw new Error("Access denied");
      throw new Error("Failed to fetch session participants");
    }
    return res.json();
  },

  async getChatHistory(
    sessionId: string,
    limit = 50,
    beforeId?: number
  ): Promise<ChatMessagesResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (beforeId) params.set("before_id", String(beforeId));
    const res = await apiFetch(
      `/api/protected/sessions/${sessionId}/messages?${params.toString()}`
    );
    if (!res.ok) throw new Error("Failed to fetch chat history");
    return res.json();
  },

  async getSessionLog(
    sessionId: string,
    limit = 100,
    beforeId?: number
  ): Promise<SessionLogResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (beforeId) params.set("before_id", String(beforeId));
    const res = await apiFetch(`/api/protected/sessions/${sessionId}/log?${params.toString()}`);
    if (!res.ok) throw new Error("Failed to fetch session log");
    return res.json();
  },

  async endSession(sessionId: string): Promise<void> {
    const res = await apiFetch(`/api/protected/sessions/${sessionId}/end`, { method: "POST" });
    if (!res.ok) {
      if (res.status === 403) throw new Error("Only the DM or campaign owner can end the session");
      if (res.status === 400) throw new Error("Session cannot be ended from its current state");
      throw new Error("Failed to end session");
    }
  },

  async deleteSession(sessionId: string): Promise<void> {
    const res = await apiFetch(`/api/protected/sessions/${sessionId}`, { method: "DELETE" });
    if (!res.ok) {
      if (res.status === 403) throw new Error("Only the campaign owner can delete sessions");
      if (res.status === 400)
        throw new Error("Active sessions cannot be deleted. End the session first.");
      throw new Error("Failed to delete session");
    }
  },

  // Keep apiFetchJson for callers that just need parsed JSON
  async joinSession(body: unknown): Promise<unknown> {
    return apiFetchJson("/api/protected/sessions/join", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};
